<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import MediaSkeleton from 'dashboard/components-next/message/media/MediaSkeleton.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const openInNewTab = () => {
  window.open(attachment.value.dataUrl, '_blank', 'noopener,noreferrer');
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <MediaSkeleton
      v-else-if="!isLoaded"
      icon="i-lucide-image"
      class="w-64"
      :aspect-ratio="
        attachment?.width && attachment?.height
          ? `${attachment.width} / ${attachment.height}`
          : '4 / 3'
      "
    />
    <div v-else class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        loading="lazy"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        :alt="$t('COMPONENTS.MEDIA.IMAGE_ALT')"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button
          xs
          solid
          slate
          icon="i-lucide-expand"
          class="opacity-60"
          :aria-label="$t('COMPONENTS.MEDIA.EXPAND')"
          @click.stop="showGallery = true"
        />
        <Button
          xs
          solid
          slate
          icon="i-lucide-external-link"
          class="opacity-60"
          :aria-label="$t('COMPONENTS.MEDIA.OPEN_IN_NEW_TAB')"
          @click.stop="openInNewTab"
        />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :aria-label="$t('COMPONENTS.MEDIA.DOWNLOAD')"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
